import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import {
  ImportShipmentLocationAddRequest,
  ShipmentLocationResponse,
  ShipmentLocationAddRequest,
  ShipmentLocationBulkDeleteRequest,
  ShipmentLocationBulkDeleteResponse,
  ShipmentLocationBulkImportResponse,
  ShipmentLocationUpdateRequest,
  ShipmentPlaceSuggestion,
  ShipmentPlace,
} from '@entities/shipment-location';
import { ApiService } from '@services/api.service';
import { UnitType } from '@components/shipment-location-list/shipment-location-edit-dialog/shipment-location-edit-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ShipmentLocationService {
  constructor(private api: ApiService) {}
  /**
   * 位置情報の一覧を取得する
   *
   * @returns 位置情報の一覧
   */
  findList(): Observable<ShipmentLocationResponse[]> {
    const url = '/shipments/locations';
    return this.api.get<ShipmentLocationResponse[]>(url);
  }

  /**
   * 位置情報を追加する
   * requestの半径の情報は単位を考慮された状態で入るものとする
   *
   * @param request 追加内容
   */
  add(request: ShipmentLocationAddRequest): Observable<void> {
    const url = '/shipments/locations';
    return this.api.post<void>(url, request);
  }

  /**
   * 位置情報を一括インポートする
   *
   * @param request 追加内容
   * @returns インポート結果
   */
  bulkImport(
    request: ImportShipmentLocationAddRequest[],
  ): Observable<ShipmentLocationBulkImportResponse> {
    const url = '/shipments/locations/_import';
    return this.api.post<ShipmentLocationBulkImportResponse>(url, request);
  }

  /**
   * 位置情報を更新する
   * requestの半径の情報は単位を考慮された状態で入るものとする
   *
   * @param locationId 位置情報ID
   * @param request 更新内容
   */
  update(
    locationId: string,
    request: ShipmentLocationUpdateRequest,
  ): Observable<void> {
    const url = `/shipments/locations/${locationId}`;
    return this.api.put<void>(url, request);
  }

  /**
   * 位置情報を削除する
   *
   * @param locationId 位置情報ID
   */
  delete(locationId: string): Observable<void> {
    const url = `/shipments/locations/${locationId}`;
    return this.api.delete<void>(url);
  }

  /**
   * 配送位置情報を一括削除する
   *
   * @param request 配送位置情報ID一覧
   * @returns 一括削除結果
   */
  deleteBulk(
    request: ShipmentLocationBulkDeleteRequest,
  ): Observable<ShipmentLocationBulkDeleteResponse> {
    const url = `/shipments/locations/_delete`;
    return this.api.post<ShipmentLocationBulkDeleteResponse>(url, request);
  }

  /**
   * ジオフェンスの単位に応じて半径を計算する
   *
   * @param unit 単位
   * @param circleRadius 単位抜きの半径
   */
  calcCircleRadius(unit: UnitType, circleRadius: number): number {
    if (unit === 'km') return circleRadius * 1000;
    else if (unit === 'mile') return circleRadius * 1609.344;
    else return circleRadius; // unit === 'm'のとき
  }

  /**
   * 入力テキストから場所候補を取得する
   *
   * @param text 入力テキスト
   * @returns 場所候補リスト
   */
  findPlaceSuggestionsList(
    text: string,
  ): Observable<ShipmentPlaceSuggestion[]> {
    const params = { text };
    const url = `/shipments/locations/places/_suggest`;
    return this.api.get<ShipmentPlaceSuggestion[]>(url, { params });
  }

  /**
   * 場所の詳細を取得する
   *
   * @param placeId 場所ID
   * @returns 場所の詳細
   */
  findPlace(placeId: string): Observable<ShipmentPlace> {
    const url = `/shipments/locations/places/${placeId}`;
    return this.api.get<ShipmentPlace>(url);
  }

  /**
   * 緯度経度から場所候補を取得する
   *
   * @param latitude 緯度
   * @param longitude 経度
   * @returns 場所リスト
   */
  findPlaceListByPosition(
    latitude: number,
    longitude: number,
  ): Observable<ShipmentPlace[]> {
    const params = { latitude, longitude };
    const url = `/shipments/locations/places`;
    return this.api.get<ShipmentPlace[]>(url, { params });
  }
}
